import { REGION } from "~/_server/constants";
import { renderEmail } from "~/_email/render";

import ApplicationReceived from "~/_email/templates/ApplicationReceived";
import ApplicationVerified from "~/_email/templates/ApplicationVerified";
import ApprovedBodyRequest from "~/_email/templates/ApprovedBodyRequest";
import ApprovedBodySubmitted from "~/_email/templates/ApprovedBodySubmitted";
import CertifierRequiringVerification from "~/_email/templates/CertifierRequiringVerification";
import CertifyDesigns from "~/_email/templates/CertifyDesigns";
import CompliancePassed from "~/_email/templates/CompliancePassed";
import ExamsPassed from "~/_email/templates/ExamsPassed";
import Invoice from "~/_email/templates/Invoice";
import SchemeUpdate from "~/_email/templates/SchemeUpdate";
import SubscriptionComplete from "~/_email/templates/SubscriptionComplete";
import SubscriptionLapsed from "~/_email/templates/SubscriptionLapsed";
import SubscriptionRenewed from "~/_email/templates/SubscriptionRenewed";
import SubscriptionRenewRequired from "~/_email/templates/SubscriptionRenewRequired";
import Welcome from "~/_email/templates/Welcome";
import RegisterUpdate from "~/_email/templates/RegisterUpdate";

import { type Attachment } from "@aws-sdk/client-sesv2";

export const sendDevEmail = async (subject: string, body: string) => {
	"use server";

	try {
		const { SESv2Client, SendEmailCommand } =
			await import("@aws-sdk/client-sesv2");

		const ses = new SESv2Client({ region: REGION });

		await ses.send(
			new SendEmailCommand({
				FromEmailAddress: "no-reply@rias-regs.co.uk",
				Destination: { ToAddresses: ["john.cotton@home-data.co.uk"] },
				Content: {
					Simple: {
						Subject: { Data: `RIAS-regs Development Email: ${subject} `, Charset: "UTF-8" },
						Body: {
							Text: { Data: body, Charset: "UTF-8" },
						},
					},
				},
			}),
		);
	} catch (err) {
		console.error("sendDevEmail failed", err);
		throw err;
	}

	return { status: "ok" };
};

export const sendEmail = async ({
	templateName,
	props,
	to,
	subject,
	from = "no-reply@rias-regs.co.uk",
	bcc = undefined,
	attachments = undefined,
}: {
	templateName: string;
	props: any;
	to: string | string[];
	subject?: string;
	from?: string;
	bcc?: string | string[] | undefined;
	attachments?: Attachment[] | undefined;
}) => {
	"use server";

	try {
		const emailArgs = getEmailArgs(templateName);

		if (emailArgs) {
			subject = subject || emailArgs.subject;
			const { html, text } = await renderEmail(emailArgs.template, props);

			const { SESv2Client, SendEmailCommand } =
				await import("@aws-sdk/client-sesv2");

			const ses = new SESv2Client({ region: REGION });

			await ses.send(
				new SendEmailCommand({
					FromEmailAddress: from,
					Destination: {
						ToAddresses: Array.isArray(to) ? to : [to],
						BccAddresses: bcc ? (Array.isArray(bcc) ? bcc : [bcc]) : undefined,
					},
					Content: {
						Simple: {
							Subject: { Data: subject },
							Body: {
								Html: { Data: html },
								Text: { Data: text },
							},
							Attachments: attachments,
						},
					},
				}),
			);
		} else {
			console.error("invalid email template", templateName);
		}
	} catch (err) {
		console.error("sendEmail failed", err);
	}
};

const getEmailArgs = (template: string) => {
	let emailArgs;
	switch ( template ) {
		case "application-received":
			emailArgs = {
				subject: "RIAS-regs Application Received",
				template: ApplicationReceived,
			};
			break;
		
		case "application-verified":
			emailArgs = {
				subject: "Your RIAS-regs application has been verified",
				template: ApplicationVerified,
			};
			break;

		case "approved-body-request":
			emailArgs = {
				subject: "Approved Body Request",
				template: ApprovedBodyRequest,
			};
			break;

		case "approved-body-submitted":
			emailArgs = {
				subject: "Approved Body Submitted",
				template: ApprovedBodySubmitted,
			};
			break;

		case "certifier-requiring-verification":
			emailArgs = {
				subject: "Certifier Requiring Verification",
				template: CertifierRequiringVerification,
			};
			break;

		case "certify-designs":
			emailArgs = {
				subject: "You Can Now Certify Designs",
				template: CertifyDesigns,
			};
			break;

		case "compliance-passed":
			emailArgs = {
				subject: "Competency Tests Passed",
				template: CompliancePassed,
			};
			break;

		case "exams-passed":
			emailArgs = {
				subject:
					"Congratulations, you have passed the On-line Competency Tests",
				template: ExamsPassed,
			};
			break;

		case "invoice":
			emailArgs = { subject: "Invoice", template: Invoice };
			break;

		case "register-update":
			emailArgs = { subject: "RIAS-regs Update", template: RegisterUpdate };
			break;

		case "scheme-update":
			emailArgs = {
				subject: "RIAS-regs Scheme Update",
				template: SchemeUpdate,
			};
			break;

		case "subscription-complete":
			emailArgs = {
				subject: "RIAS-regs Certification Subscription Complete",
				template: SubscriptionComplete,
			};
			break;

		case "subscription-lapsed":
			emailArgs = {
				subject: "RIAS-regs Subscription Lapsed",
				template: SubscriptionLapsed,
			};
			break;
	
		case "subscription-renewed":
			emailArgs = {
				subject: "RIAS-regs Subscription Renewed",
				template: SubscriptionRenewed,
			};
			break;

		case "subscription-renewal-required":
			emailArgs = {
				subject: "RIAS-regs Subscription Renewal Required",
				template: SubscriptionRenewRequired,
			};
			break;

		case "welcome":
			emailArgs = { subject: "Welcome", template: Welcome };
			break;

		default:
			break;
	}
	return emailArgs;
};
